import { memo } from 'react';
import type { NodeProps } from '@xyflow/react';
import type { MeasurementNode as TMeasurementNode } from '../store';

function MeasurementNodeImpl({ data, selected }: NodeProps<TMeasurementNode>) {
  const width = Math.max(1, data.width ?? 1);
  const height = Math.max(1, data.height ?? 1);
  const scale = data.scalePxPerMm || 1;
  const distancePx = Math.hypot(width, height);
  const distanceMm = distancePx / scale;
  const label = data.label || `${formatMm(distanceMm)} mm`;
  const pad = 16;
  const svgWidth = width + pad * 2;
  const svgHeight = height + pad * 2;
  // Draw along the actual measured direction: the bounding box's main diagonal
  // (top-left → bottom-right) by default, or the anti-diagonal (bottom-left →
  // top-right) when the segment was drawn that way.
  const x1 = pad;
  const y1 = data.antiDiagonal ? pad + height : pad;
  const x2 = pad + width;
  const y2 = data.antiDiagonal ? pad : pad + height;
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;

  return (
    <div
      style={{
        width: svgWidth,
        height: svgHeight,
        transform: `translate(${-pad}px, ${-pad}px)`,
        pointerEvents: 'all',
        userSelect: 'none',
      }}
      title={label}
    >
      <svg width={svgWidth} height={svgHeight} style={{ overflow: 'visible' }}>
        <defs>
          <marker
            id="measure-arrow"
            markerWidth="8"
            markerHeight="8"
            refX="4"
            refY="4"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" fill="#f97316" />
          </marker>
        </defs>
        <line
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="#f97316"
          strokeWidth={selected ? 2.5 : 2}
          markerStart="url(#measure-arrow)"
          markerEnd="url(#measure-arrow)"
        />
        <circle cx={x1} cy={y1} r={3.5} fill="#fff" stroke="#f97316" strokeWidth={1.5} />
        <circle cx={x2} cy={y2} r={3.5} fill="#fff" stroke="#f97316" strokeWidth={1.5} />
        <rect
          x={midX - Math.max(34, label.length * 3.6)}
          y={midY - 9}
          width={Math.max(68, label.length * 7.2)}
          height={18}
          rx={3}
          fill="var(--panel)"
          stroke={selected ? '#f97316' : 'var(--panel-border)'}
        />
        <text
          x={midX}
          y={midY + 4}
          textAnchor="middle"
          fontSize="10"
          fontWeight="700"
          fill="var(--text)"
          style={{ fontFamily: 'ui-monospace, Consolas, monospace' }}
        >
          {label}
        </text>
      </svg>
    </div>
  );
}

function formatMm(value: number) {
  if (value >= 100) return String(Math.round(value));
  if (value >= 10) return String(Math.round(value * 10) / 10);
  return String(Math.round(value * 100) / 100);
}

export const MeasurementNode = memo(MeasurementNodeImpl);
